import Phaser from 'phaser';
import {
  FONT_FAMILY,
  GAME_WIDTH,
  getGameHeight,
} from '../config/constants';
import type { BoardGeometry } from '../config/BoardGeometry';
import { getGameplayColorHex } from '../config/colors';
import { BoardSystem, cellKey, type MegaBlock, type LockedDoor, type BlockadeWall, type Barricade } from '../systems/BoardSystem';
import { CubeSprite } from '../entities/Cube';
import { SnakeOverlay } from '../entities/SnakeOverlay';
import { crispText } from '../ui/textUtils';
import { reducedParticles } from '../config/particleConfig';
import { drawGradientBackground } from '../ui/background';
import { soundManager } from '../audio/SoundManager';

/** Lightweight data for a cube cell on the RenderTexture (no game object). */
interface CubeData {
  row: number;
  col: number;
  colorIndex: number;
  isSnake: boolean;
}

/** Sprite entry for board elements that have an HP counter. */
interface HpSpriteEntry {
  container: Phaser.GameObjects.Container;
  hpText: Phaser.GameObjects.Text;
}

/** Flash a container briefly to a lower alpha (hit feedback). */
function flashHit(scene: Phaser.Scene, container: Phaser.GameObjects.Container, flashAlpha = 0.6): void {
  scene.tweens.add({
    targets: container,
    alpha: flashAlpha,
    duration: 60,
    yoyo: true,
    ease: 'Sine.easeOut',
  });
}

/** Create an HP label centered in a container of the given dimensions. */
function createHpLabel(
  scene: Phaser.Scene, hp: number, w: number, h: number, sizeFactor = 0.5, yOffset = 0,
): Phaser.GameObjects.Text {
  const fontSize = Math.min(w, h) * sizeFactor;
  return crispText(scene.add.text(0, yOffset, `${hp}`, {
    fontFamily: FONT_FAMILY,
    fontSize: `${Math.max(fontSize, 12)}px`,
    color: '#ffffff',
    fontStyle: 'bold',
    stroke: '#000000',
    strokeThickness: fontSize > 20 ? 4 : 3,
  })).setOrigin(0.5, 0.5);
}

/**
 * Owns all board-related visuals: cubes, snakes, mega blocks,
 * locked doors, blockade walls, and barricades.
 *
 * GameScene delegates initial drawing and hit/update animations to this class.
 */
export class BoardRenderer {
  private scene: Phaser.Scene;
  private board: BoardSystem;
  private geometry: BoardGeometry;

  /** Visual palette hex colors (undefined for old levels). */
  private visualPalette?: string[];
  /** Visual grid: each cell is an index into visualPalette (-1 = empty). Same dimensions as board grid. */
  private visualGrid?: number[][];
  /** Maps each visualPalette index to a GAME_COLORS index for gameplay matching. */
  private gameplayMap?: number[];
  /** Blend factor: 0 = pure gameplay colors, 1 = pure visual colors. Default 1. */
  private blendFactor = 1;

  // ── RenderTexture for batched cube rendering ──────────────────────────
  private boardRT!: Phaser.GameObjects.RenderTexture;
  /** Second RT showing gameplay colors, sits behind boardRT for crossfade blending. */
  private gameplayRT?: Phaser.GameObjects.RenderTexture;
  /** Lightweight data for each cube cell (no game object). */
  readonly cubeData = new Map<number, CubeData>();

  // Overlays (still individual game objects — small count, need animations)
  readonly snakeOverlays = new Map<number, SnakeOverlay>();
  readonly megaBlockSprites = new Map<number, HpSpriteEntry>();
  readonly lockedDoorSprites = new Map<number, HpSpriteEntry>();
  readonly blockadeWallSprites = new Map<number, HpSpriteEntry>();
  readonly barricadeSprites = new Map<number, HpSpriteEntry>();

  /** Reusable stamp image for drawing cubes onto the RenderTexture. */
  private stamp!: Phaser.GameObjects.Image;
  /** Fully opaque square stamp for erasing cells cleanly from the RenderTexture. */
  private eraseStamp!: Phaser.GameObjects.Image;

  constructor(scene: Phaser.Scene, board: BoardSystem, geometry: BoardGeometry, visualPalette?: string[], visualGrid?: number[][], gameplayMap?: number[]) {
    this.scene = scene;
    this.board = board;
    this.geometry = geometry;
    this.visualPalette = visualPalette;
    this.visualGrid = visualGrid;
    this.gameplayMap = gameplayMap;

    const { cols, rows, cellSize, offsetX, offsetY } = this.geometry;

    // Create the board-sized RenderTexture(s)
    const bw = cols * cellSize;
    const bh = rows * cellSize;

    if (this.visualPalette && this.visualGrid && this.gameplayMap) {
      // Dual-RT mode: gameplay RT behind, visual RT on top for alpha crossfade
      this.gameplayRT = scene.add.renderTexture(
        offsetX, offsetY, bw, bh,
      ).setOrigin(0, 0).setDepth(0);

      this.boardRT = scene.add.renderTexture(
        offsetX, offsetY, bw, bh,
      ).setOrigin(0, 0).setDepth(1);
    } else {
      this.boardRT = scene.add.renderTexture(
        offsetX, offsetY, bw, bh,
      ).setOrigin(0, 0).setDepth(0);
    }

    // Create a reusable stamp (not added to display list)
    this.stamp = new Phaser.GameObjects.Image(scene, 0, 0, 'cube-0');
    this.stamp.setDisplaySize(cellSize, cellSize);

    // Create a fully opaque white square texture for erasing cells cleanly
    if (!scene.textures.exists('_erase-square')) {
      const eraseCanvas = scene.textures.createCanvas('_erase-square', 32, 32)!;
      const ctx = eraseCanvas.getContext();
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 32, 32);
      eraseCanvas.refresh();
    }
    this.eraseStamp = new Phaser.GameObjects.Image(scene, 0, 0, '_erase-square');
    this.eraseStamp.setDisplaySize(cellSize, cellSize);
  }

  // ── Initial drawing ──────────────────────────────────────────────────

  static drawBackground(scene: Phaser.Scene): void {
    drawGradientBackground(scene, -10);

    const dots = scene.add.graphics().setDepth(-9).setAlpha(0.06);
    const spacing = 40;
    for (let x = spacing; x < GAME_WIDTH; x += spacing) {
      for (let y = spacing; y < getGameHeight(); y += spacing) {
        dots.fillStyle(0xaaaacc, 1);
        dots.fillCircle(x, y, 2);
      }
    }
  }

  drawBoardBackground(): void {
    const { offsetX, offsetY, boardWidth: bw, boardHeight: bh } = this.geometry;
    const cx = offsetX + bw / 2;
    const cy = offsetY + bh / 2;

    this.scene.add.rectangle(cx + 4, cy + 6, bw + 8, bh + 8, 0x000000).setAlpha(0.3).setDepth(-2);
    this.scene.add.rectangle(cx, cy, bw, bh, 0x1e1e3a).setDepth(-1);
    const border = this.scene.add.graphics().setDepth(-1);
    border.lineStyle(2, 0x3a3a5a, 0.5);
    border.strokeRect(offsetX, offsetY, bw, bh);
  }

  drawBoard(): void {
    // Draw mega blocks first (so regular cubes don't overlap)
    for (const [, mb] of this.board.getMegaBlocks()) {
      this.createMegaBlockSprite(mb);
    }
    for (let row = 0; row < this.board.rows; row++) {
      for (let col = 0; col < this.board.cols; col++) {
        if (this.board.isMegaBlockCell(row, col)) continue;
        if (this.board.isDoorCell(row, col)) continue;
        if (this.board.isBarricadeCell(row, col)) continue;
        const topColor = this.board.getTopColor(row, col);
        if (topColor !== null) {
          const isSnake = this.board.isSnakeCell(row, col);
          if (this.visualPalette && this.visualGrid) {
            const visualIndex = this.visualGrid[row]?.[col] ?? -1;
            if (visualIndex >= 0) {
              this.stampVisualCube(row, col, visualIndex, isSnake);
              // Also stamp gameplay colors into the gameplay RT for crossfade
              if (this.gameplayRT && this.gameplayMap) {
                this.stampGameplayCube(row, col, visualIndex);
              }
            }
          } else {
            this.stampCube(row, col, topColor, isSnake);
          }
        }
      }
    }
    // Draw locked doors on top of everything
    for (const [, door] of this.board.getActiveDoors()) {
      this.createLockedDoorSprite(door);
    }
  }

  drawSnakeOverlays(): void {
    for (const [, overlay] of this.snakeOverlays) {
      overlay.destroy();
    }
    this.snakeOverlays.clear();

    const snakeCells = this.board.getSnakeCells();
    if (snakeCells.size === 0) return;

    const colMap = new Map<number, number[]>();
    for (const key of snakeCells) {
      const row = Math.floor(key / 256);
      const col = key % 256;
      let rows = colMap.get(col);
      if (!rows) {
        rows = [];
        colMap.set(col, rows);
      }
      rows.push(row);
    }

    for (const [col, rows] of colMap) {
      rows.sort((a, b) => a - b);
      const topRow = rows[0];
      const bottomRow = rows[rows.length - 1];

      let colorIndex = 0;
      for (const row of rows) {
        const top = this.board.getTopColor(row, col);
        if (top !== null) {
          colorIndex = top;
          break;
        }
      }

      // Erase individual snake cubes from the RenderTexture — the overlay will draw on top
      for (const row of rows) {
        const key = cellKey(row, col);
        if (this.cubeData.has(key)) {
          this.eraseCube(row, col);
        }
      }

      const overlay = new SnakeOverlay(this.scene, col, topRow, bottomRow, colorIndex, this.geometry);
      this.snakeOverlays.set(col, overlay);
    }
  }

  drawBlockadeWalls(): void {
    for (const [, wall] of this.board.getActiveBlockadeWalls()) {
      this.createBlockadeWallSprite(wall);
    }
  }

  drawBarricades(): void {
    for (const barricade of this.board.getActiveBarricades()) {
      this.createBarricadeSprite(barricade);
    }
  }

  // ── RenderTexture cube helpers ───────────────────────────────────────

  /** Stamp a single cube onto the RenderTexture and record its data. */
  private stampCube(row: number, col: number, colorIndex: number, isSnake = false): void {
    const key = cellKey(row, col);
    this.cubeData.set(key, { row, col, colorIndex, isSnake });
    const { cellSize } = this.geometry;

    const textureKey = isSnake ? `snake-cube-${colorIndex}` : `cube-${colorIndex}`;
    this.stamp.setTexture(textureKey);
    this.stamp.setDisplaySize(cellSize, cellSize);

    // RenderTexture coordinates are local (0,0 is top-left of the RT)
    const localX = col * cellSize + cellSize / 2;
    const localY = row * cellSize + cellSize / 2;
    this.boardRT.draw(this.stamp, localX, localY);
  }

  /**
   * Stamp a single cube using a visual hex color (filled rect, no texture).
   * Used for visual palette levels where each cell has a unique hex color.
   * Always stamps the PURE visual color — crossfade blending is handled by RT alpha.
   */
  private stampVisualCube(row: number, col: number, visualIndex: number, isSnake = false): void {
    const key = cellKey(row, col);
    this.cubeData.set(key, { row, col, colorIndex: visualIndex, isSnake });
    const { cellSize } = this.geometry;

    const hexColor = this.visualPalette![visualIndex];
    if (!hexColor) return;
    const numericColor = parseInt(hexColor.slice(1), 16);

    const localX = col * cellSize;
    const localY = row * cellSize;

    const gfx = new Phaser.GameObjects.Graphics(this.scene);
    gfx.fillStyle(numericColor, 1);
    gfx.fillRect(0, 0, cellSize, cellSize);
    this.boardRT.draw(gfx, localX, localY);
    gfx.destroy();
  }

  /**
   * Stamp a single cube with its gameplay color into the gameplayRT.
   * Maps visualPalette index → gameplayMap → GAME_COLORS.
   */
  private stampGameplayCube(row: number, col: number, visualIndex: number): void {
    if (!this.gameplayRT || !this.gameplayMap) return;
    const { cellSize } = this.geometry;

    const gameplayIndex = this.gameplayMap[visualIndex];
    if (gameplayIndex === undefined || gameplayIndex < 0) return;
    const gpHex = getGameplayColorHex(gameplayIndex);

    const localX = col * cellSize;
    const localY = row * cellSize;

    const gfx = new Phaser.GameObjects.Graphics(this.scene);
    gfx.fillStyle(gpHex, 1);
    gfx.fillRect(0, 0, cellSize, cellSize);
    this.gameplayRT.draw(gfx, localX, localY);
    gfx.destroy();
  }

  /** Lerp between two 0xRRGGBB colors by factor t (0 = colorA, 1 = colorB). */
  private static lerpColor(a: number, b: number, t: number): number {
    const rA = (a >> 16) & 0xff, gA = (a >> 8) & 0xff, bA = a & 0xff;
    const rB = (b >> 16) & 0xff, gB = (b >> 8) & 0xff, bB = b & 0xff;
    const r = Math.round(rA + (rB - rA) * t);
    const g = Math.round(gA + (gB - gA) * t);
    const bl = Math.round(bA + (bB - bA) * t);
    return (r << 16) | (g << 8) | bl;
  }

  /** Returns true if this renderer has visual palette data. */
  hasVisualPalette(): boolean {
    return !!(this.visualPalette && this.visualGrid);
  }

  /** Get the blended numeric color for a visual palette index, respecting current blendFactor. */
  private getBlendedVisualColor(visualIndex: number): number {
    const hexColor = this.visualPalette![visualIndex];
    if (!hexColor) return 0xffffff;
    let numericColor = parseInt(hexColor.slice(1), 16);

    if (this.blendFactor < 1 && this.gameplayMap) {
      const gameplayIndex = this.gameplayMap[visualIndex];
      if (gameplayIndex !== undefined && gameplayIndex >= 0) {
        const gpHex = getGameplayColorHex(gameplayIndex);
        numericColor = BoardRenderer.lerpColor(gpHex, numericColor, this.blendFactor);
      }
    }

    return numericColor;
  }

  /**
   * Set the blend factor (0 = gameplay colors, 1 = visual colors).
   * In dual-RT mode this just changes the visual RT's alpha — instant, no redraw.
   * Falls back to full redraw for non-dual-RT levels.
   */
  setBlendFactor(factor: number): void {
    this.blendFactor = Math.max(0, Math.min(1, factor));
    if (this.gameplayRT) {
      // Dual-RT crossfade: just set the visual overlay's alpha
      this.boardRT.setAlpha(this.blendFactor);
    } else {
      this.redrawAllCubes();
    }
  }

  /** Set the blend factor without redrawing. */
  setBlendFactorQuiet(factor: number): void {
    this.blendFactor = Math.max(0, Math.min(1, factor));
  }

  /** Public wrapper to trigger a full board redraw. */
  redrawBoard(): void {
    this.redrawAllCubes();
  }

  /** Redraw all existing cubes on both RenderTextures. */
  private redrawAllCubes(): void {
    this.boardRT.clear();
    if (this.gameplayRT) this.gameplayRT.clear();

    // Re-stamp all cubes from cubeData
    const entries = Array.from(this.cubeData.values());
    for (const data of entries) {
      if (this.visualPalette && this.visualGrid) {
        this.stampVisualCube(data.row, data.col, data.colorIndex, data.isSnake);
        if (this.gameplayRT && this.gameplayMap) {
          this.stampGameplayCube(data.row, data.col, data.colorIndex);
        }
      } else {
        this.stampCube(data.row, data.col, data.colorIndex, data.isSnake);
      }
    }

    // Re-erase cells covered by active snake overlays so they don't double-render
    for (const [col] of this.snakeOverlays) {
      for (const data of entries) {
        if (data.col === col && data.isSnake) {
          this.eraseCube(data.row, data.col);
        }
      }
    }
  }

  /** Erase a single cube area from both RenderTextures (visual + gameplay). */
  private eraseCube(row: number, col: number): void {
    const { cellSize } = this.geometry;
    const localX = col * cellSize + cellSize / 2;
    const localY = row * cellSize + cellSize / 2;
    this.boardRT.erase(this.eraseStamp, localX, localY);
    if (this.gameplayRT) {
      this.gameplayRT.erase(this.eraseStamp, localX, localY);
    }
  }



  private createMegaBlockSprite(mb: MegaBlock): void {
    const { offsetX, offsetY, cellSize } = this.geometry;
    const x = offsetX + mb.col * cellSize;
    const y = offsetY + mb.row * cellSize;
    const w = mb.width * cellSize;
    const h = mb.height * cellSize;
    const color = getGameplayColorHex(mb.colorIndex);

    const container = this.scene.add.container(x + w / 2, y + h / 2);

    const bg = this.scene.add.graphics();
    bg.fillStyle(color, 1);
    bg.fillRoundedRect(-w / 2, -h / 2, w, h, 4);
    bg.lineStyle(3, 0xffffff, 0.6);
    bg.strokeRoundedRect(-w / 2, -h / 2, w, h, 4);
    bg.lineStyle(1, 0xffffff, 0.3);
    bg.strokeRoundedRect(-w / 2 + 2, -h / 2 + 2, w - 4, h - 4, 2);
    container.add(bg);

    const hpText = createHpLabel(this.scene, mb.hp, w, h, 0.5);
    container.add(hpText);

    container.setDepth(5);
    this.megaBlockSprites.set(mb.id, { container, hpText });
  }

  private createLockedDoorSprite(door: LockedDoor): void {
    const { offsetX, offsetY, cellSize } = this.geometry;
    const x = offsetX + door.col * cellSize;
    const y = offsetY + door.row * cellSize;
    const w = door.width * cellSize;
    const h = door.height * cellSize;
    const color = getGameplayColorHex(door.colorIndex);

    const container = this.scene.add.container(x + w / 2, y + h / 2);

    const bg = this.scene.add.graphics();
    bg.fillStyle(0x1a1a2e, 0.92);
    bg.fillRoundedRect(-w / 2, -h / 2, w, h, 8);
    bg.lineStyle(4, color, 0.9);
    bg.strokeRoundedRect(-w / 2, -h / 2, w, h, 8);
    bg.lineStyle(1, 0xffffff, 0.15);
    bg.strokeRoundedRect(-w / 2 + 4, -h / 2 + 4, w - 8, h - 8, 6);
    container.add(bg);

    const orbGraphics = this.scene.add.graphics();
    const orbRadius = Math.min(w, h) * 0.08;
    orbGraphics.fillStyle(color, 1);
    orbGraphics.fillCircle(0, -h * 0.25, orbRadius);
    orbGraphics.fillStyle(color, 0.3);
    orbGraphics.fillCircle(0, -h * 0.25, orbRadius * 1.6);
    container.add(orbGraphics);

    const lockGraphics = this.scene.add.graphics();
    const lockSize = Math.min(w, h) * 0.12;
    lockGraphics.fillStyle(0xffffff, 0.25);
    lockGraphics.fillRoundedRect(-lockSize * 0.6, -h * 0.08, lockSize * 1.2, lockSize, 4);
    lockGraphics.lineStyle(3, 0xffffff, 0.25);
    lockGraphics.beginPath();
    lockGraphics.arc(0, -h * 0.08, lockSize * 0.45, Math.PI, 0, false);
    lockGraphics.strokePath();
    container.add(lockGraphics);

    const hpText = createHpLabel(this.scene, door.hp, w, h, 0.35, h * 0.15);
    container.add(hpText);

    container.setDepth(8);
    this.lockedDoorSprites.set(door.id, { container, hpText });
  }

  private createBlockadeWallSprite(wall: BlockadeWall): void {
    const { offsetX, offsetY, cellSize, boardWidth, boardHeight } = this.geometry;
    const color = getGameplayColorHex(wall.colorIndex);
    const wallThickness = cellSize * 2.5;
    let x: number, y: number, w: number, h: number;

    const boardLeft = offsetX;
    const boardTop = offsetY;
    const boardRight = offsetX + boardWidth;
    const boardBottom = offsetY + boardHeight;

    switch (wall.side) {
      case 'left':
        x = boardLeft - wallThickness;
        y = boardTop + wall.startCell * cellSize;
        w = wallThickness;
        h = wall.length * cellSize;
        break;
      case 'right':
        x = boardRight;
        y = boardTop + wall.startCell * cellSize;
        w = wallThickness;
        h = wall.length * cellSize;
        break;
      case 'top':
        x = boardLeft + wall.startCell * cellSize;
        y = boardTop - wallThickness;
        w = wall.length * cellSize;
        h = wallThickness;
        break;
      case 'bottom':
        x = boardLeft + wall.startCell * cellSize;
        y = boardBottom;
        w = wall.length * cellSize;
        h = wallThickness;
        break;
    }

    const container = this.scene.add.container(x + w / 2, y + h / 2);

    const bg = this.scene.add.graphics();
    bg.fillStyle(color, 0.85);
    bg.fillRoundedRect(-w / 2, -h / 2, w, h, 6);
    bg.lineStyle(3, 0x000000, 0.5);
    bg.strokeRoundedRect(-w / 2, -h / 2, w, h, 6);
    bg.fillStyle(0xffffff, 0.15);
    bg.fillRoundedRect(-w / 2 + 4, -h / 2 + 4, w - 8, h - 8, 4);
    container.add(bg);

    const stripes = this.scene.add.graphics();
    stripes.lineStyle(2, 0x000000, 0.15);
    const stripeSpacing = 12;
    if (wall.side === 'left' || wall.side === 'right') {
      for (let sy = -h / 2 + stripeSpacing; sy < h / 2; sy += stripeSpacing) {
        stripes.lineBetween(-w / 2 + 4, sy, w / 2 - 4, sy);
      }
    } else {
      for (let sx = -w / 2 + stripeSpacing; sx < w / 2; sx += stripeSpacing) {
        stripes.lineBetween(sx, -h / 2 + 4, sx, h / 2 - 4);
      }
    }
    container.add(stripes);

    const hpText = createHpLabel(this.scene, wall.hp, w, h, 0.45);
    container.add(hpText);

    container.setDepth(9);
    this.blockadeWallSprites.set(wall.id, { container, hpText });
  }

  private createBarricadeSprite(barricade: Barricade): void {
    const { offsetX, offsetY, cellSize } = this.geometry;
    const x = offsetX + barricade.col * cellSize;
    const y = offsetY + barricade.row * cellSize;
    const w = barricade.width * cellSize;
    const h = barricade.height * cellSize;

    const container = this.scene.add.container(x + w / 2, y + h / 2);

    const bg = this.scene.add.graphics();
    bg.fillStyle(0x8B6914, 0.92);
    bg.fillRoundedRect(-w / 2, -h / 2, w, h, 6);
    bg.lineStyle(4, 0x5C4A1A, 0.9);
    bg.strokeRoundedRect(-w / 2, -h / 2, w, h, 6);
    container.add(bg);

    const plankGraphics = this.scene.add.graphics();
    plankGraphics.lineStyle(2, 0x5C4A1A, 0.4);
    const numPlanks = Math.max(2, Math.floor(h / (cellSize * 0.8)));
    for (let i = 1; i < numPlanks; i++) {
      const plankY = -h / 2 + (h / numPlanks) * i;
      plankGraphics.beginPath();
      plankGraphics.moveTo(-w / 2 + 4, plankY);
      plankGraphics.lineTo(w / 2 - 4, plankY);
      plankGraphics.strokePath();
    }
    container.add(plankGraphics);

    const nailGraphics = this.scene.add.graphics();
    nailGraphics.fillStyle(0x888888, 0.8);
    const nailR = 3;
    const inset = 8;
    nailGraphics.fillCircle(-w / 2 + inset, -h / 2 + inset, nailR);
    nailGraphics.fillCircle(w / 2 - inset, -h / 2 + inset, nailR);
    nailGraphics.fillCircle(-w / 2 + inset, h / 2 - inset, nailR);
    nailGraphics.fillCircle(w / 2 - inset, h / 2 - inset, nailR);
    container.add(nailGraphics);

    const hpText = createHpLabel(this.scene, barricade.hp, w, h, 0.35);
    container.add(hpText);

    container.setDepth(8);
    this.barricadeSprites.set(barricade.id, { container, hpText });
  }

  // ── Hit / update handlers ────────────────────────────────────────────

  handleCubeHit(row: number, col: number): void {
    const key = cellKey(row, col);
    const data = this.cubeData.get(key);
    soundManager.randomPop();

    const snakeHandled = this.handleSnakeShrink(col);

    if (data) {
      // Erase from RenderTexture
      this.eraseCube(row, col);
      this.cubeData.delete(key);

      if (!snakeHandled) {
        const { offsetX, offsetY, cellSize } = this.geometry;
        const x = offsetX + col * cellSize + cellSize / 2;
        const y = offsetY + row * cellSize + cellSize / 2;

        if (this.visualPalette && this.visualGrid) {
          // Visual palette: use blended color for the destroy animation
          const numericColor = this.getBlendedVisualColor(data.colorIndex);
          const tempGfx = this.scene.add.graphics();
          tempGfx.fillStyle(numericColor, 1);
          tempGfx.fillRect(-cellSize / 2, -cellSize / 2, cellSize, cellSize);
          tempGfx.setPosition(x, y);

          // Particle burst (skipped during win speed-up)
          if (!reducedParticles) {
            const emitter = this.scene.add.particles(x, y, 'particle', {
              speed: { min: 30, max: 90 },
              scale: { start: 0.9, end: 0 },
              alpha: { start: 1, end: 0 },
              lifespan: 350,
              tint: numericColor,
              emitting: false,
            });
            emitter.explode(6);
            this.scene.time.delayedCall(400, () => emitter.destroy());
          }

          // Pop + fade
          this.scene.tweens.add({
            targets: tempGfx,
            scaleX: 1.4,
            scaleY: 1.4,
            alpha: 0,
            duration: 200,
            ease: 'Back.easeIn',
            onComplete: () => tempGfx.destroy(),
          });
        } else {
          // Spawn a temporary sprite for the destroy animation
          const tempSprite = new CubeSprite(this.scene, row, col, x, y, data.colorIndex, data.isSnake, cellSize);
          tempSprite.destroyWithAnimation();
        }
      }
    }
  }

  handleSnakeShrink(col: number): boolean {
    const overlay = this.snakeOverlays.get(col);
    if (!overlay) return false;

    const alive = overlay.shrinkFromBottom();
    if (!alive) {
      this.snakeOverlays.delete(col);
    }
    return true;
  }

  handleMegaBlockHit(megaBlockId: number, hpRemaining: number): void {
    const entry = this.megaBlockSprites.get(megaBlockId);
    if (!entry) return;

    if (hpRemaining <= 0) {
      this.destroyOverlayWithBurst(entry.container, {
        tint: 0xffffff,
        particleCount: 16,
        speedMin: 50,
        speedMax: 150,
        scaleStart: 1.2,
        lifespan: 500,
        emitterDelay: 600,
        tweenScaleX: 1.3,
        tweenScaleY: 1.3,
        tweenDuration: 250,
        onComplete: () => this.megaBlockSprites.delete(megaBlockId),
      });
    } else {
      entry.hpText.setText(`${hpRemaining}`);
      flashHit(this.scene, entry.container);
    }
  }

  handleLockedDoorHpUpdate(doorId: number, hpRemaining: number, maxHp: number): void {
    const entry = this.lockedDoorSprites.get(doorId);
    if (!entry) return;

    entry.hpText.setText(`${hpRemaining}`);

    const progress = 1 - (hpRemaining / maxHp);
    const alpha = 1 - progress * 0.3;
    entry.container.setAlpha(alpha);
    flashHit(this.scene, entry.container, alpha * 0.5);
  }

  handleLockedDoorUnlock(door: LockedDoor): void {
    const entry = this.lockedDoorSprites.get(door.id);
    if (!entry) return;

    const color = getGameplayColorHex(door.colorIndex);

    this.destroyOverlayWithBurst(entry.container, {
      tint: color,
      particleCount: 24,
      tweenScaleX: 1.4,
      tweenScaleY: 1.4,
      tweenDuration: 350,
      onComplete: () => {
        this.lockedDoorSprites.delete(door.id);
        this.revealCubesUnder(door.row, door.col, door.width, door.height, 15, true);
      },
    });
  }

  handleBlockadeWallHpUpdate(wallId: number, hpRemaining: number, maxHp: number): void {
    const entry = this.blockadeWallSprites.get(wallId);
    if (!entry) return;

    entry.hpText.setText(`${hpRemaining}`);

    const ratio = hpRemaining / maxHp;
    const scaleMultiplier = 0.4 + 0.6 * ratio;
    const alpha = 0.5 + 0.5 * ratio;

    this.scene.tweens.add({
      targets: entry.container,
      scaleX: scaleMultiplier,
      scaleY: scaleMultiplier,
      alpha,
      duration: 150,
      ease: 'Sine.easeOut',
    });
    flashHit(this.scene, entry.container, alpha * 0.4);
  }

  handleBlockadeWallDestroy(wallId: number, colorIndex: number): void {
    const entry = this.blockadeWallSprites.get(wallId);
    if (!entry) return;

    const color = getGameplayColorHex(colorIndex);

    this.destroyOverlayWithBurst(entry.container, {
      tint: color,
      particleCount: 20,
      speedMax: 200,
      tweenScaleX: 0,
      tweenScaleY: 0,
      tweenDuration: 300,
      onComplete: () => this.blockadeWallSprites.delete(wallId),
    });
  }

  handleBarricadeHpUpdate(barricadeId: number, hpRemaining: number, maxHp: number): void {
    const entry = this.barricadeSprites.get(barricadeId);
    if (!entry) return;

    entry.hpText.setText(`${hpRemaining}`);

    const progress = 1 - (hpRemaining / maxHp);
    const alpha = 1 - progress * 0.3;
    entry.container.setAlpha(alpha);
    flashHit(this.scene, entry.container, alpha * 0.5);
  }

  handleBarricadeDestroy(barricade: Barricade): void {
    const entry = this.barricadeSprites.get(barricade.id);
    if (!entry) return;

    this.destroyOverlayWithBurst(entry.container, {
      tint: 0x8B6914,
      particleCount: 24,
      tweenScaleX: 1.4,
      tweenScaleY: 1.4,
      tweenDuration: 350,
      onComplete: () => {
        this.barricadeSprites.delete(barricade.id);
        this.revealCubesUnder(barricade.row, barricade.col, barricade.width, barricade.height, 30);
      },
    });
  }

  /** Shared helper for overlay destroy animations (mega block, locked door, blockade wall, barricade). */

  /**
   * Reveal cubes hidden under a destroyed overlay (locked door or barricade).
   * Creates temporary sprite for the pop-in animation, then stamps to the RenderTexture.
   */
  private revealCubesUnder(
    row: number, col: number, width: number, height: number,
    delayPerCell: number, fadeAlpha = false,
  ): void {
    const { offsetX, offsetY, cellSize } = this.geometry;
    for (let r = row; r < row + height; r++) {
      for (let c = col; c < col + width; c++) {
        if (r >= this.board.rows || c >= this.board.cols) continue;
        if (this.board.isMegaBlockCell(r, c)) continue;
        const topColor = this.board.getTopColor(r, c);
        if (topColor !== null) {
          const isSnake = this.board.isSnakeCell(r, c);
          const x = offsetX + c * cellSize + cellSize / 2;
          const y = offsetY + r * cellSize + cellSize / 2;

          const cellRow = r;
          const cellCol = c;
          const cellColor = topColor;
          const cellIsSnake = isSnake;
          const delay = ((r - row) * width + (c - col)) * delayPerCell;

          if (this.visualPalette && this.visualGrid) {
            const visualIndex = this.visualGrid[r]?.[c] ?? -1;
            if (visualIndex >= 0) {
              // Use blended color for the reveal animation
              const numericColor = this.getBlendedVisualColor(visualIndex);
              const tempGfx = this.scene.add.graphics();
              tempGfx.fillStyle(numericColor, 1);
              tempGfx.fillRect(-cellSize / 2, -cellSize / 2, cellSize, cellSize);
              tempGfx.setPosition(x, y);
              tempGfx.setScale(0);
              if (fadeAlpha) tempGfx.setAlpha(0);

              this.scene.tweens.add({
                targets: tempGfx,
                scaleX: 1,
                scaleY: 1,
                ...(fadeAlpha ? { alpha: 1 } : {}),
                duration: 250,
                ease: 'Back.easeOut',
                delay,
                onComplete: () => {
                  tempGfx.destroy();
                  this.stampVisualCube(cellRow, cellCol, visualIndex, cellIsSnake);
                  if (this.gameplayRT && this.gameplayMap) {
                    this.stampGameplayCube(cellRow, cellCol, visualIndex);
                  }
                },
              });
            }
          } else {
            const textureKey = isSnake ? `snake-cube-${topColor}` : `cube-${topColor}`;

            // Create a temporary sprite for the animation
            const tempSprite = this.scene.add.image(x, y, textureKey);
            tempSprite.setDisplaySize(cellSize, cellSize);
            const targetScaleX = tempSprite.scaleX;
            const targetScaleY = tempSprite.scaleY;
            tempSprite.setScale(0);
            if (fadeAlpha) tempSprite.setAlpha(0);

            this.scene.tweens.add({
              targets: tempSprite,
              scaleX: targetScaleX,
              scaleY: targetScaleY,
              ...(fadeAlpha ? { alpha: 1 } : {}),
              duration: 250,
              ease: 'Back.easeOut',
              delay,
              onComplete: () => {
                // Remove the temp sprite and stamp onto the RenderTexture
                tempSprite.destroy();
                this.stampCube(cellRow, cellCol, cellColor, cellIsSnake);
              },
            });
          }
        }
      }
    }
  }

  private destroyOverlayWithBurst(
    container: Phaser.GameObjects.Container,
    opts: {
      tint: number;
      particleCount: number;
      speedMin?: number;
      speedMax?: number;
      scaleStart?: number;
      lifespan?: number;
      emitterDelay?: number;
      tweenScaleX: number;
      tweenScaleY: number;
      tweenDuration: number;
      onComplete?: () => void;
    },
  ): void {
    soundManager.randomPop();

    // Reduce or skip particles during win speed-up to avoid frame drops
    const particleCount = reducedParticles ? Math.ceil(opts.particleCount / 4) : opts.particleCount;
    if (particleCount > 0) {
      const emitter = this.scene.add.particles(container.x, container.y, 'particle', {
        speed: { min: opts.speedMin ?? 60, max: opts.speedMax ?? 180 },
        scale: { start: opts.scaleStart ?? 1.5, end: 0 },
        alpha: { start: 1, end: 0 },
        lifespan: opts.lifespan ?? 600,
        tint: opts.tint,
        emitting: false,
      });
      emitter.explode(particleCount);
      this.scene.time.delayedCall(opts.emitterDelay ?? 700, () => emitter.destroy());
    }

    this.scene.tweens.add({
      targets: container,
      scaleX: opts.tweenScaleX,
      scaleY: opts.tweenScaleY,
      alpha: 0,
      duration: opts.tweenDuration,
      ease: 'Back.easeIn',
      onComplete: () => {
        container.destroy();
        opts.onComplete?.();
      },
    });
  }

  /** Clean up all sprite maps and references. Called on scene shutdown. */
  destroy(): void {
    this.cubeData.clear();
    this.snakeOverlays.clear();
    this.megaBlockSprites.clear();
    this.lockedDoorSprites.clear();
    this.blockadeWallSprites.clear();
    this.barricadeSprites.clear();
    if (this.stamp) this.stamp.destroy();
    if (this.eraseStamp) this.eraseStamp.destroy();
    if (this.boardRT) this.boardRT.destroy();
    if (this.gameplayRT) this.gameplayRT.destroy();
  }
}
